import React from 'react';
import { usePermission, useAnyPermission, useAllPermissions } from '../../hooks/usePermission';

interface PermissionGuardProps {
  permission?: string;          // Single permission
  anyPermissions?: string[];    // Any of these permissions (OR)
  allPermissions?: string[];    // All of these permissions (AND)
  fallback?: React.ReactNode;   // What to show if no permission
  children: React.ReactNode;
}

/**
 * Component that renders children only if user has required permissions
 *
 * Usage:
 * <PermissionGuard permission="agents:create">
 *   <Button>Create Agent</Button>
 * </PermissionGuard>
 */
export const PermissionGuard: React.FC<PermissionGuardProps> = ({
  permission,
  anyPermissions,
  allPermissions,
  fallback = null,
  children
}) => {
  const hasSinglePermission = usePermission(permission || '');
  const hasAnyPermission = useAnyPermission(anyPermissions || []);
  const hasAllPermissions = useAllPermissions(allPermissions || []);

  let hasAccess = false;

  if (permission) {
    hasAccess = hasSinglePermission;
  } else if (anyPermissions) {
    hasAccess = hasAnyPermission;
  } else if (allPermissions) {
    hasAccess = hasAllPermissions;
  }

  return hasAccess ? <>{children}</> : <>{fallback}</>;
};

export default PermissionGuard;